import React, { FunctionComponent, useMemo } from 'react';

import noop from 'lodash/noop';
import { useSelector } from 'react-redux';

import { isMessengerIntegration } from '../../../../../../shared/features/config/selectors';
import { goloomSdk } from '../../../../../../shared/features/goloom-sdk';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { useActions } from '../../../../../../shared/hooks';
import { ConferenceStatus } from '../../../../constants';
import { useLocalMediaButtonStatus } from '../../../../hooks/use-local-media-button-status';
import {
    getConferenceStatus,
    getDesktopSharingStatus,
    getJoinedAsGuest,
    getRoomId,
    isLocalRecordingActive,
    isSelfModerator,
    selectVisibleParticipantsList,
} from '../../../../selectors';
import { DisconnectReason } from '../../../../types';
import { cancelCallAction } from '../../../conference/slice';
import { toggleMuteAction } from '../../../device';
import { isRoleModelEnabledSelector } from '../../../permissions';
import { useToolbarButtonClickHandlerMaker } from '../../../toolbar/hooks';
import { CameraButton } from '../../../toolbar/toolbar-buttons/camera-button';
import { EndCallButton } from '../../../toolbar/toolbar-buttons/end-call-button';
import { MicrophoneButton } from '../../../toolbar/toolbar-buttons/microphone-button';
import { PiPGoloomVideoGrid } from '../pip-goloom-video-grid';
import styles from './styles.css';

export const PiPCallLayout: FunctionComponent = () => {
    const { toggleMute, cancel } = useActions({ toggleMute: toggleMuteAction, cancel: cancelCallAction });
    const makeAction = useToolbarButtonClickHandlerMaker();
    const roomId = useSelector(getRoomId);

    const conferenceStatus = useSelector(getConferenceStatus);
    const isJoined = conferenceStatus === ConferenceStatus.JOINED;
    const joinedAsGuest = useSelector(getJoinedAsGuest);
    const isDesktopJoining = !isJoined && joinedAsGuest;
    const participantsCount = useSelector((state) =>
        getConferenceStatus(state) === ConferenceStatus.JOINED ? selectVisibleParticipantsList(state).length : undefined
    );

    const isModerator = useSelector(isSelfModerator);
    const isRoleModelEnabled = useSelector(isRoleModelEnabledSelector);
    const messengerIntegration = useSelector(isMessengerIntegration);

    const desktopSharingStatus = useSelector(getDesktopSharingStatus);
    const isSharingDesktop = desktopSharingStatus === 'local';
    const isRecording = useSelector(isLocalRecordingActive);

    const shouldShowEndCallOptions = useMemo(
        () =>
            isRoleModelEnabled &&
            isModerator &&
            (isJoined || isDesktopJoining) &&
            participantsCount !== undefined &&
            participantsCount > 1,
        [isRoleModelEnabled, isModerator, isJoined, isDesktopJoining, participantsCount]
    );

    const endCallOnClick = () => cancel({ source: DisconnectReason.END_CALL_BUTTON });
    const endCallClickMetrika = makeAction(noop, 'drop', roomId);
    const withEndCallAccent = (!isDesktopJoining || messengerIntegration) && !isSharingDesktop && !isRecording;

    const { audioStatus, videoStatus, isAudioMuted, isVideoMuted } = useLocalMediaButtonStatus();

    return (
        <div className={styles.container}>
            <div className={styles.grid}>{goloomSdk.session && <PiPGoloomVideoGrid session={goloomSdk.session} />}</div>
            <div className={styles.toolbar}>
                <div className={styles.leftBar}>
                    <MicrophoneButton
                        buttonSize="lg"
                        status={audioStatus}
                        onToggleMute={() => toggleMute({ type: 'audio' })}
                        title={isAudioMuted ? i18n('toolbar', 'turn-on-mic') : i18n('toolbar', 'turn-off-mic')}
                        testId={isAudioMuted ? 'turn-on-mic-button' : 'turn-off-mic-button'}
                        key="toggleAudioButton"
                    />
                    <CameraButton
                        buttonSize="lg"
                        status={videoStatus}
                        onToggleMute={() => toggleMute({ type: 'video' })}
                        title={isVideoMuted ? i18n('toolbar', 'turn-on-camera') : i18n('toolbar', 'turn-off-camera')}
                        testId={isVideoMuted ? 'turn-on-camera-button' : 'turn-off-camera-button'}
                        key="toggleVideoButton"
                    />
                </div>
                <div className={styles.rightBar}>
                    <EndCallButton
                        onClick={endCallOnClick}
                        clickMetrika={endCallClickMetrika}
                        shouldShowOptions={shouldShowEndCallOptions}
                        withAccent={withEndCallAccent}
                        key="endCallButton"
                    />
                </div>
            </div>
        </div>
    );
};
